import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, MapPin, Expand, X } from "lucide-react";
import { format, parseISO } from "date-fns";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

// Image Preview Modal
const ImagePreviewModal = ({ isOpen, onClose, src, alt }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md"
        onClick={onClose}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-10"
        >
          <X className="w-6 h-6" />
        </button>

        <motion.img
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          src={src}
          alt={alt}
          className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />

        {alt && (
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-lg font-medium bg-black/50 px-4 py-2 rounded-lg"
          >
            {alt}
          </motion.p>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export const ClassCard = ({ schedule, onBook, expired = false }) => {
  const [previewOpen, setPreviewOpen] = useState(false);

  const danceClass = schedule.dance_class || {};
  const displayTitle = schedule.title || danceClass.name || "Dance Class";
  const instructor = schedule.instructor || {};
  const studio = schedule.studio || {};

  const startTime = schedule.start_datetime ? parseISO(schedule.start_datetime) : new Date();
  const currentBookings = schedule.current_bookings || 0;
  const spotsLeft = (schedule.max_capacity || 20) - currentBookings;
  const isFull = spotsLeft <= 0;
  const isLowSpots = spotsLeft > 0 && spotsLeft <= 5;
  const isDisabled = isFull || expired;

  const basePrice = schedule.price || danceClass.price || 0;
  const discountPct = schedule.discount_percentage || 0;
  const finalPrice = discountPct > 0 ? (schedule.discounted_price ?? basePrice) : basePrice;

  const imageSrc = schedule.image || danceClass.image || "https://images.unsplash.com/photo-1508700929628-666bc8bd84ea?w=800";

  const levelColors = {
    beginner: "bg-green-500/20 text-green-400 border-green-500/30",
    intermediate: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    advanced: "bg-red-500/20 text-red-400 border-red-500/30",
    all_levels: "bg-empire-gold/20 text-empire-gold border-empire-gold/30",
  };

  const handlePreviewClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPreviewOpen(true);
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ y: expired ? 0 : -4 }}
        className={`card-empire overflow-hidden group ${expired ? "opacity-60" : ""}`}
        data-testid={`class-card-${schedule.id}`}
      >
        {/* Image */}
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            src={imageSrc}
            alt={displayTitle}
            className={`w-full h-full object-cover transition-transform duration-500 ${expired ? "" : "group-hover:scale-105"}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

          {/* Level badge */}
          <div className="absolute top-3 left-3">
            <Badge className={`${levelColors[danceClass.difficulty_level] || levelColors.all_levels} border`}>
              {(danceClass.difficulty_level || "all_levels").replace("_", " ")}
            </Badge>
          </div>

          {/* Status indicator */}
          {expired ? (
            <div className="absolute top-3 right-12">
              <Badge className="bg-neutral-500/40 text-neutral-300 border border-neutral-500/30">Expired</Badge>
            </div>
          ) : isFull ? (
            <div className="absolute top-3 right-12">
              <Badge className="bg-red-500/20 text-red-400 border border-red-500/30">Full</Badge>
            </div>
          ) : isLowSpots ? (
            <div className="absolute top-3 right-12">
              <Badge className="bg-orange-500/20 text-orange-400 border border-orange-500/30 animate-pulse">
                {spotsLeft} spots left
              </Badge>
            </div>
          ) : null}

          {/* Preview Button */}
          <button
            onClick={handlePreviewClick}
            className="absolute top-3 right-3 p-2 bg-black/60 hover:bg-black/80 backdrop-blur-sm rounded-lg text-white transition-all opacity-0 group-hover:opacity-100 z-10"
            title="View full image"
          >
            <Expand className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {/* Time */}
          <div className="flex items-center gap-2 text-empire-gold text-sm font-semibold mb-2">
            <Clock className="w-4 h-4" />
            <span>{format(startTime, "EEEE, MMM d")}</span>
            <span className="text-neutral-500">•</span>
            <span>{format(startTime, "h:mm a")}</span>
          </div>

          {/* Class name */}
          <h3 className={`text-xl font-bold mb-2 transition-colors ${expired ? "text-neutral-400" : "text-white group-hover:text-empire-gold"}`}>
            {displayTitle}
          </h3>

          {/* Instructor */}
          {instructor.name && (
            <p className="text-neutral-400 text-sm mb-3">
              with <span className={expired ? "text-neutral-400" : "text-white"}>{instructor.name}</span>
            </p>
          )}

          {/* Meta info */}
          <div className="flex items-center gap-4 text-sm text-neutral-500 mb-4">
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              <span>{danceClass.studio_name || studio.name || "Empire Dance Studios"}</span>
            </div>
          </div>

          {/* Price and Book */}
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              {discountPct > 0 && (
                <span className="text-xs text-neutral-500 line-through">
                  KES {basePrice.toLocaleString()}
                </span>
              )}
              <div className="flex items-center gap-2">
                <span className={`text-2xl font-bold ${expired ? "text-neutral-500" : "text-empire-gold"}`}>
                  KES {finalPrice.toLocaleString()}
                </span>
                {discountPct > 0 && (
                  <span className="text-xs font-bold text-green-400 bg-green-500/15 border border-green-500/30 px-1.5 py-0.5 rounded">
                    -{Math.round(discountPct)}%
                  </span>
                )}
              </div>
            </div>
            <Button
              onClick={() => !isDisabled && onBook(schedule)}
              disabled={isDisabled}
              className={`${isDisabled
                ? "bg-neutral-800 text-neutral-500 cursor-not-allowed"
                : "btn-empire"
                }`}
              data-testid={`book-btn-${schedule.id}`}
            >
              {expired ? "Expired" : isFull ? "Full" : "Book Now"}
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Image Preview Modal */}
      <ImagePreviewModal
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        src={imageSrc}
        alt={displayTitle}
      />
    </>
  );
};
